import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import {
  searchDeployedInterfacePageEmbeddedEntities,
  useFindDeployedEntityById,
  useSearchDeployedInterfacePageEmbeddedEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import type { EmbeddedDependentsResponse } from '@unifyapps/network/generated/models/embeddedDependentsResponse';
import { UAError } from '@unifyapps/network/utils/consts/error';
import { useMemo } from 'react';
import { debugSSR } from '../../../../../../../consts/debug';
import { useEnvVariables } from '../../../../../../../hooks/useEnvVariables';
import { useFetchInterfacePermissions } from '../../../../../../access-control/hooks/useFetchInterfacePermissions';
import { applyInterfaceOverride } from '../../../../../../interfaceOverride/applyInterfaceOverride';
import { useFetchDeployedInterfaceOverride } from '../../../../../../interfaceOverride/useFetchDeployedInterfaceOverride';
import { InterfaceClient } from '../../../../../../stores/InterfaceStore';
import InterfaceHelper from '../../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import {
  DEPLOYED_ALLOWED_ENTITY_TYPES,
  EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
} from '../../../../../../utils/interfaceQueryFilters';
import { normalizeEmbeddedDependents } from '../../../../../../utils/normalizeEmbeddedDependents';
import type { UseFetchInterfaceAppResourcesHookType } from '../../../../types';
import { useGetAuthDeployedInterface } from '../../useAuthDeployedInterface';
import { useFetchDeployedInterfaceCommonNamespace } from '../../useFetchDeployedInterfaceNamespaces';

const useFetchDeployedInterfaceAppResources: UseFetchInterfaceAppResourcesHookType = ({
  interfaceId,
  client,
  mode,
  disableAuth = false,
}) => {
  /**
   * Below two queries are ran conditionally based on client prop
   * Now lets say in a parent component(eg: InterfacePageComponent.tsx) we want useFindDeployedEntityById
   * hence enabled is true for useFindDeployedEntityById and false for useGetAuthDeployedInterface
   *
   * Now lets say in an app there is a module being rendered which will use the useGetAuthDeployedInterface query
   * Hence in that case enabled is true for useGetAuthDeployedInterface and false for useFindDeployedEntityById
   * Now when useGetAuthDeployedInterface query is in flight as the module requested it, the same query mounted in parent component
   * will also have isLoading as true. Now because of this the parent component will render the loader and not the child component.
   * This will lead to state reset as the store providers will unmount and mount again.
   *
   * Hence, to avoid this, the effective isLoading should be only be true if that query is enabled.
   *
   * Note: getHost() was previously used here to check if we have a valid host before querying auth deployed interface.
   * However, getHost() returns "" (empty string) during SSR, causing Boolean(getHost()) to be false.
   * This made shouldQueryAuthDeployedInterface = false during SSR even when client === UA_MATRIX.
   *
   * As a result, the code always fell into the useFindDeployedEntityById branch during SSR.
   * But useFindDeployedEntityById is also disabled for UA_MATRIX (shouldQueryDeployedInterfaceById = false).
   * Data only worked intermittently when prefetch happened to populate the cache in time.
   *
   * The fallback `authEntityInterface.data || data` masked the bug:
   * - If the code had been `authEntityInterface.data` (without `|| data`), the bug would have surfaced
   *   immediately since authEntityInterface.data would be undefined when the wrong branch was taken.
   *
   * Fix: Removed getHost() check. The client prop alone is sufficient to determine the query strategy.
   */
  // const shouldQueryAuthDeployedInterface = Boolean(getHost()) && client === InterfaceClient.UA_MATRIX && !disableAuth;
  const shouldQueryAuthDeployedInterface = client === InterfaceClient.UA_MATRIX && !disableAuth;
  const authEntityInterface = useGetAuthDeployedInterface<InterfaceEntity>({
    // UA_MATRIX is for matrix app, so there we fetch from auth/interface endpoint that uses domain
    query: {
      enabled: shouldQueryAuthDeployedInterface,
      retry: 3,
      staleTime: Infinity,
    },
  });
  const isLoadingAuthDeployedInterfaceQuery =
    authEntityInterface.isLoading && shouldQueryAuthDeployedInterface;

  const shouldQueryDeployedInterfaceById =
    (Boolean(interfaceId) && client === InterfaceClient.UA_PLATFORM) || disableAuth;
  const { data, isLoading } = useFindDeployedEntityById<InterfaceEntity>(
    UAEntityTypes.Interface,
    interfaceId,
    {
      query: {
        enabled: shouldQueryDeployedInterfaceById,
        retry: 3,
        staleTime: Infinity,
      },
    },
  );

  const baseInterfaceRecord = shouldQueryAuthDeployedInterface
    ? authEntityInterface.data || data
    : data;

  // single chokepoint: merge the override onto the base here so BOTH the GlobalAppStateStore and the
  // InterfaceStore seed from one effective record. gated on the flag; hydrates from the prefetched key.
  // the override rides whichever route resolved the base: domain-resolved auth endpoint for UA_MATRIX,
  // authenticated entity search for the UA_PLATFORM by-id path.
  const { override, isLoading: isLoadingOverride } = useFetchDeployedInterfaceOverride({
    interfaceId: baseInterfaceRecord?.id,
    isOverridden: InterfaceHelper.isInterfaceOverridden(baseInterfaceRecord),
    source: shouldQueryAuthDeployedInterface ? 'auth-domain' : 'search',
  });
  const interfaceRecord = useMemo(
    () =>
      override && baseInterfaceRecord
        ? applyInterfaceOverride(baseInterfaceRecord, override)
        : baseInterfaceRecord,
    [baseInterfaceRecord, override],
  );

  const {
    commonNamespace,
    isLoading: isLoadingCommonNamespace,
    error: errorCommonNamespace,
  } = useFetchDeployedInterfaceCommonNamespace({
    interfaceId,
    interfaceRecord,
    mode,
  });

  const isLoadingDeployedInterfaceByIdQuery = isLoading && shouldQueryDeployedInterfaceById;

  const isLoadingDeployedInterfaceQuery =
    isLoadingAuthDeployedInterfaceQuery || isLoadingDeployedInterfaceByIdQuery;
  const errorInterfaceRecord = authEntityInterface.error;

  const {
    envVariables,
    isLoading: envVariablesLoading,
    error: envVariablesError,
  } = useEnvVariables();

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useFetchInterfacePermissions({
    interfaceId,
    client,
    mode,
  });

  const { id: globalInterfacePageId } =
    InterfaceHelper.getGlobalInterfacePageComponent(interfaceId);

  // One embedded-entities call returns the global page entity AND every data source it
  // references — replaces the legacy split (deployed Component search + deployed DataSource search).
  const globalInterfacePageRequest = {
    entityId: globalInterfacePageId,
    excludeFields: EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
    allowedEntityTypes: DEPLOYED_ALLOWED_ENTITY_TYPES,
  };

  const {
    data: embeddedResp,
    isLoading: isLoadingGlobalInterfacePage,
    error: errorGlobalInterfacePage,
  } = useSearchDeployedInterfacePageEmbeddedEntities(globalInterfacePageRequest, {
    query: {
      enabled: Boolean(globalInterfacePageId),
      retry: 3,
      staleTime: Infinity,
      // A missing global page is an expected state, not a failure: older apps never had one, and
      // in builder useCreateGlobalInterfacePageEffect lazily creates it on first mount. The
      // backend returns ASSET_NOT_FOUND (5002) for it, and an errored query is exempt from
      // staleTime — React Query refires it on every remount, so during first-page creation the
      // load/error toggles unmount and remount the app shell in a loop. Resolving 5002 as an
      // empty success (normalizeEmbeddedDependents yields undefined page + empty dataSources)
      // caches it under staleTime: Infinity like the pre-5002 backend contract, so it refetches
      // only on explicit invalidation (post-create invalidation, builder evict-on-mount).
      // Genuine failures still reject and retry.
      queryFn: async ({ signal, meta }) => {
        try {
          return await searchDeployedInterfacePageEmbeddedEntities(
            globalInterfacePageRequest,
            { meta },
            signal,
          );
        } catch (error) {
          const errorCode = (error as { payload?: { errorCode?: UAError } } | undefined)?.payload
            ?.errorCode;
          if (errorCode === UAError.ASSET_NOT_FOUND) {
            return {} as EmbeddedDependentsResponse;
          }
          throw error;
        }
      },
    },
  });

  const { interfacePage: globalInterfacePage, dataSources } = useMemo(
    () => normalizeEmbeddedDependents(embeddedResp, true),
    [embeddedResp],
  );

  const _isLoading =
    isLoadingDeployedInterfaceQuery ||
    isLoadingOverride ||
    permissionsLoading ||
    envVariablesLoading ||
    isLoadingCommonNamespace ||
    isLoadingGlobalInterfacePage;

  const _error =
    errorInterfaceRecord ||
    permissionsError ||
    envVariablesError ||
    errorCommonNamespace ||
    errorGlobalInterfacePage;

  debugSSR(
    'useFetchDeployedInterfaceAppResources',
    {
      isLoading: _isLoading,
      isLoadingDeployedInterfaceQuery,
      isLoadingCommonNamespace,
      isLoadingPermissions: permissionsLoading,
      isLoadingEnvVariables: envVariablesLoading,
      isLoadingGlobalInterfacePage,
    },
    {
      interfaceId: interfaceRecord?.id,
    },
  );

  return {
    isLoading: _isLoading,
    error: _error,
    interfaceRecord,
    permissions,
    envVariables,
    commonNamespace,
    dataSources,
    globalInterfacePage,
  };
};

export default useFetchDeployedInterfaceAppResources;
